import { collection, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { api, db } from '../firebase';
import { addDays, prettyDate, today } from '../lib/dates';
import { t } from '../lib/i18n';
import { streaks } from '../lib/streaks';
import type { Completion } from '../lib/types';
import { useApp } from '../state';

interface Review { weekStart: string; summary: string; focus: string[] }

/** Streaks, a cheer for the partner and the latest Sunday review, on Today. */
export function DuoBoost() {
  const { duo, user, tasks, partner, members } = useApp();
  const uid = user!.uid;
  const [ticks, setTicks] = useState<Completion[]>([]);
  const [review, setReview] = useState<Review | null>(null);
  const [personal, setPersonal] = useState<{ weekStart: string; text: string } | null>(null);
  const [cheer, setCheer] = useState<'' | 'sending' | 'sent' | 'error'>('');
  const from = addDays(today(), -60);

  useEffect(() => {
    if (!duo) return;
    const offs = [
      onSnapshot(query(collection(db, 'duos', duo.id, 'completions'), where('date', '>=', from)), (s) => setTicks(s.docs.map((d) => d.data() as Completion)), () => setTicks([])),
      onSnapshot(query(collection(db, 'duos', duo.id, 'reviews'), orderBy('weekStart', 'desc'), limit(1)), (s) => setReview(s.empty ? null : (s.docs[0].data() as Review)), () => setReview(null)),
      onSnapshot(doc(db, 'users', uid, 'private', 'review'), (s) => setPersonal(s.exists() ? (s.data() as { weekStart: string; text: string }) : null), () => setPersonal(null)),
    ];
    return () => offs.forEach((f) => f());
  }, [duo, uid, from]);

  const s = useMemo(() => streaks(tasks, ticks, members.map((m) => m.uid), today()), [tasks, ticks, members]);
  const fresh = review && review.weekStart >= addDays(today(), -9);
  const mine = s.byUid[uid] ?? 0;

  async function sendCheer() {
    setCheer('sending');
    try { await api.cheerPartner(); setCheer('sent'); } catch { setCheer('error'); }
  }

  return (
    <>
      <section className="streaks">
        <span className="streak"><b className="num">🔥 {mine}</b>{t('day streak')}</span>
        {partner && <span className="streak"><b className="num">💞 {s.both}</b>{t('both did it')}</span>}
        {partner && (
          <button className="btn slim cheer" onClick={sendCheer} disabled={cheer === 'sending' || cheer === 'sent'}>
            {cheer === 'sent' ? t('Cheer sent 👏') : t('👏 Cheer {name}', { name: partner.displayName })}
          </button>
        )}
      </section>
      {cheer === 'error' && <p className="error small">{t('Could not send the cheer. Try again later.')}</p>}
      {fresh && (
        <details className="card review" open={review.weekStart >= addDays(today(), -2)}>
          <summary><b>{t('Sunday review')}</b> <span className="muted small">{t('week of {date}', { date: prettyDate(review.weekStart, { day: 'numeric', month: 'short' }) })}</span></summary>
          <p>{review.summary}</p>
          {review.focus?.length > 0 && <><p className="eyebrow">{t('Next week')}</p><ul className="clean">{review.focus.map((f) => <li key={f}>{f}</li>)}</ul></>}
          {personal?.weekStart === review.weekStart && personal.text && <p className="hint">🔒 {personal.text}</p>}
        </details>
      )}
    </>
  );
}
